import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Fingerprint, MapPin, Camera, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin/dashboard" : "/staff/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = mode === "login" ? await login(email, password) : await register(name, email, password);
      navigate(u.role === "admin" ? "/admin/dashboard" : "/staff/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setForgotMsg("");
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotMsg("Jika email terdaftar, link reset telah dikirim ke email Anda (berlaku 1 jam).");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Brand / hero */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#1F1B16] p-12 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D96635 0, transparent 40%), radial-gradient(circle at 80% 80%, #E8A24F 0, transparent 40%)" }} />
        <div className="relative z-10 flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center overflow-hidden p-1.5">
            <img src="/logo-mark.png" alt="BedsolvingOps" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">BedsolvingOps</div>
            <div className="text-[11px] text-slate-400 tracking-wide">Seamless Workflow &amp; Transparent Yield</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Selfie-verified attendance for your remote team.
          </h1>
          <p className="text-slate-300 text-base max-w-md">
            Track check-ins, monitor live work status, and export payroll-ready reports — built for home-base staff.
          </p>
          <div className="space-y-3 pt-2">
            {[
              { icon: Camera, t: "Photo proof on every check-in & check-out" },
              { icon: MapPin, t: "Work zone & device metadata captured" },
              { icon: ShieldCheck, t: "Live monitoring dashboard for HR" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-200">
                <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <f.icon className="h-4 w-4" />
                </div>
                <span className="text-sm">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500 font-mono">© 2026 BedsolvingOps · Home Base Edition</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-[#1F1B16] flex items-center justify-center overflow-hidden p-1.5">
              <img src="/logo-mark.png" alt="BedsolvingOps" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-display text-lg font-bold leading-none">BedsolvingOps</div>
              <div className="text-[11px] text-slate-500">Seamless Workflow &amp; Transparent Yield</div>
            </div>
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
            Welcome back
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            Sign in to record your attendance.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Pratama" required data-testid="name-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="password-input" />
            </div>

            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full h-11 text-base" disabled={busy} data-testid="auth-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>

            <div className="text-center">
              <button type="button" onClick={() => { setForgotOpen((v) => !v); setForgotMsg(""); setError(""); }} className="text-sm text-[#D96635] hover:underline" data-testid="forgot-password-link">
                Lupa password?
              </button>
            </div>
          </form>

          {forgotOpen && (
            <form onSubmit={submitForgot} className="mt-4 rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="forgot-form">
              <p className="text-sm text-slate-600">Masukkan email akun Anda — kami kirim link reset password (berlaku 1 jam).</p>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="forgot-email-input" />
              {forgotMsg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="forgot-success">{forgotMsg}</div>}
              <Button type="submit" variant="outline" className="w-full" disabled={busy} data-testid="forgot-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim link reset"}
              </Button>
            </form>
          )}

          <p className="mt-6 text-sm text-slate-400 text-center">
            Accounts are created by your admin. Contact HR if you need access.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
